<template>
  <b-modal
    v-model="isOpen"
    :can-cancel="false"
    width="847px"
    data-testid="ppm-modal"
  >
    <component
      :is="screenComponent"
      :payment-account="paymentAccount"
      @go-to-add-payment-method="goToAddPaymentMethod"
      @go-to-add-payment-method-more-info="goToAddPaymentMethodMoreInfo"
      @go-to-add-payment-method-form="goToAddPaymentMethodForm"
      @go-to-financials="goToFinancials"
      @go-to-select-preferred-payment-method="goToSelectPreferredPaymentMethod"
      @save-preferred-payment-method="savePreferredPaymentMethod"
      @close="closeModal"
    />
  </b-modal>
</template>

<script>
import { getGraphql } from '../../static/graphql/getGraphql'
import AddPaymentMethodScreen from './screens/AddPaymentMethodScreen.vue'
import DirectDepositMoreInfoScreen from './screens/direct-deposit/DirectDepositMoreInfo.vue'
import DirectDepositScreen from './screens/direct-deposit/DirectDepositScreen.vue'
import ErrorScreen from './screens/ErrorScreen.vue'
import LoadingScreen from './screens/LoadingScreen.vue'
import PhysicalCardMoreInfoScreen from './screens/physical-card/PhysicalCardMoreInfo.vue'
import PhysicalCardScreen from './screens/physical-card/PhysicalCardScreen.vue'
import SelectPaymentMethodScreen from './screens/SelectPaymentMethodScreen.vue'
import SuccessScreen from './screens/SuccessScreen.vue'
import VirtualCardMoreInfoScreen from './screens/virtual-card/VirtualCardMoreInfo.vue'
import VirtualCardScreen from './screens/virtual-card/VirtualCardScreen.vue'
import WelcomeScreen from './screens/WelcomeScreen.vue'
import WelcomeScreenWithoutPaymentMethod from './screens/WelcomeScreenWithoutPaymentMethod.vue'
import PaymentFlagsMixin from './mixins/PaymentFlagsMixin'
import PreferredPaymentMethodMixin from './mixins/PreferredPaymentMethodMixin'

const screens = {
  welcome: WelcomeScreen,
  welcomeWithoutPaymentMethod: WelcomeScreenWithoutPaymentMethod,
  loading: LoadingScreen,
  addPaymentMethod: AddPaymentMethodScreen,
  selectPaymentMethod: SelectPaymentMethodScreen,
  directDeposit: DirectDepositScreen,
  virtualCard: VirtualCardScreen,
  physicalCard: PhysicalCardScreen,
  success: SuccessScreen,
  error: ErrorScreen,
  directDepositMoreInfo: DirectDepositMoreInfoScreen,
  physicalCardMoreInfo: PhysicalCardMoreInfoScreen,
  virtualCardMoreInfo: VirtualCardMoreInfoScreen,
}

export default {
  mixins: [PaymentFlagsMixin, PreferredPaymentMethodMixin],
  data() {
    return {
      isOpen: false,
      currentScreen: 'welcomeWithoutPaymentMethod',
      paymentAccount: null,
      paymentAccounts: [],
    }
  },
  computed: {
    screenComponent() {
      return screens[this.currentScreen]
    },
  },
  created() {
    this.setupModal()
  },
  methods: {
    async savePreferredPaymentMethod(entityPaymentUuid) {
      if (entityPaymentUuid) {
        this.currentScreen = 'loading'
        await this.persistPPM(entityPaymentUuid)
        this.setPaymentAccount(entityPaymentUuid)
        this.currentScreen = 'success'
      } else {
        this.currentScreen = 'error'
      }
    },
    async goToWelcome() {
      const paymentAccounts = await this.getPaymentAccounts()
      if (paymentAccounts.length) {
        this.currentScreen = 'welcome'
      } else {
        this.currentScreen = 'welcomeWithoutPaymentMethod'
      }
      this.paymentAccounts = paymentAccounts
    },
    goToAddPaymentMethod() {
      this.currentScreen = 'addPaymentMethod'
    },
    async goToAddPaymentMethodMoreInfo(paymentOption) {
      if (await this.isPaymentMethodPresent(paymentOption)) return
      switch (paymentOption) {
        case 'STRIPE_DIRECT_DEPOSIT':
          this.currentScreen = 'directDepositMoreInfo'
          break
        case 'STRIPE_CARD_ISSUING_DIGITAL':
          this.currentScreen = 'virtualCardMoreInfo'
          break
        case 'STRIPE_CARD_ISSUING_PHYSICAL':
          this.currentScreen = 'physicalCardMoreInfo'
          break
      }
    },
    goToAddPaymentMethodForm(paymentOption) {
      switch (paymentOption) {
        case 'STRIPE_DIRECT_DEPOSIT':
          this.currentScreen = 'directDeposit'
          break
        case 'STRIPE_CARD_ISSUING_DIGITAL':
          this.currentScreen = 'virtualCard'
          break
        case 'STRIPE_CARD_ISSUING_PHYSICAL':
          this.currentScreen = 'physicalCard'
          break
      }
    },
    goToSelectPreferredPaymentMethod() {
      this.currentScreen = 'selectPaymentMethod'
    },
    goToFinancials() {
      this.isOpen = false
      this.$router.push(this.localePath('financials'))
    },
    closeModal() {
      this.isOpen = false
    },
    async setupModal() {
      try {
        // Some routes don't have authentication setup
        // Preferred Payment Method should only appear for authenticated users
        if (!this.$auth.loggedIn) return

        const hasFlagEnabled = await this.$featureFlag.isEnabled([
          { name: 'ff_ppm_modal' },
          { name: 'ff_usio_payment_enabled' },
        ])

        if (
          !hasFlagEnabled.ff_ppm_modal ||
          hasFlagEnabled.ff_usio_payment_enabled
        )
          return

        const query = await getGraphql('query-has-future-payments.graphql')
        const response = await this.$axios.post('/GRAPHQL_URL/', {
          query,
        })
        const hasPPM = response.data.data.me.ppmStatus.ppm
        const hasQueuedPayments =
          response.data.data.me.ppmStatus.hasQueuedPayments

        if (hasQueuedPayments && !hasPPM) {
          await this.goToWelcome()
          this.isOpen = true
          this.$analytics.sendEvent('ppm_modal_started', {
            initialScreen: this.currentScreen,
            hasQueuedPayments,
            hasPPM: Boolean(hasPPM),
          })
        }
      } catch (error) {
        console.error('ERROR GETTING PPM', error)
      }
    },
    setPaymentAccount(entityPaymentUuid) {
      this.paymentAccount = this.paymentAccounts.find(
        (account) =>
          account.entity_payment &&
          account.entity_payment.entity_payment_uuid === entityPaymentUuid
      )
    },
  },
}
</script>

<style></style>
